import { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import ID from '@/_helpers/uuid';
import { fetchWrapper } from '@/_helpers/fetchWrapper';
import { showLoadingAlert } from '../../FormBuilder/alerts';
import { AUTO_DETECT_ALIGNMENT, AUTO_DETECT_INSERT_DELAY_MS } from '../constants';

const DETECTED_FIELD_TYPES = {
    date: { key: 'DatePicker', type: 'date' },
    signature: { key: 'Signature', type: 'signature' },
    email: { key: 'Email', type: 'textbox' },
    select: { key: 'Select', type: 'select' },
    header: { key: 'Header', type: 'textbox' },
};

const createCoordinateConfig = ({ bbox, sourceDimensions, canvas, pdfDimensions, page }) => {
    if (!bbox || !sourceDimensions?.width || !sourceDimensions?.height) return null;

    const x = Math.max(0, Math.min(1, bbox.x / sourceDimensions.width + AUTO_DETECT_ALIGNMENT.xOffset));
    const y = Math.max(0, Math.min(1, bbox.y / sourceDimensions.height + AUTO_DETECT_ALIGNMENT.yOffset));
    const width = Math.max(
        AUTO_DETECT_ALIGNMENT.minRelativeWidth,
        Math.min(1 - x, bbox.width / sourceDimensions.width + AUTO_DETECT_ALIGNMENT.widthOffset)
    );
    const height = Math.max(
        AUTO_DETECT_ALIGNMENT.minRelativeHeight,
        Math.min(1 - y, bbox.height / sourceDimensions.height + AUTO_DETECT_ALIGNMENT.heightOffset)
    );
    const canvasWidth = canvas?.clientWidth || pdfDimensions.width;
    const canvasHeight = canvas?.clientHeight || pdfDimensions.height;

    return {
        templateConfig: { x, y, width, height, page },
        rectConfig: {
            x: x * canvasWidth,
            y: y * canvasHeight,
            width: width * canvasWidth,
            height: height * canvasHeight,
            page,
        },
    };
};

export const createDetectedFormElement = ({ field, detectionMetadata, canvas, pdfDimensions }) => {
    const page = field?.page || 1;
    const sourceDimensions = detectionMetadata?.pageDimensions?.[page] || detectionMetadata?.dimensions;
    const coordinates = createCoordinateConfig({
        bbox: field?.bbox,
        sourceDimensions,
        canvas,
        pdfDimensions,
        page,
    });
    if (!coordinates) return null;

    const { templateConfig, rectConfig } = coordinates;
    const fieldName = field.label || `Detected field ${page}`;
    const detectedType = field.detectedType || field.type;
    const formElementType =
        field.key && field.type
            ? { key: field.key, type: field.type }
            : DETECTED_FIELD_TYPES[detectedType] || { key: 'InputField', type: 'textbox' };
    const id = ID.uuid();

    if (formElementType.key === 'Checkbox' || formElementType.key === 'Radio') {
        const key = formElementType.key;
        const detectedOptions = field.options?.length ? field.options : [{ label: fieldName, bbox: field.bbox }];
        return {
            id,
            key,
            type: formElementType.type,
            fieldName,
            required: false,
            config: { hasTemplateConfig: true },
            data: detectedOptions
                .map((option) => {
                    const optionCoordinates = createCoordinateConfig({
                        bbox: option.bbox,
                        sourceDimensions,
                        canvas,
                        pdfDimensions,
                        page,
                    });
                    if (!optionCoordinates) return null;
                    return {
                        text: option.label,
                        value: ID.uuid(),
                        config: {
                            parentFormElement: { id, key },
                            ...optionCoordinates,
                        },
                    };
                })
                .filter(Boolean),
        };
    }

    if (formElementType.key === 'Select') {
        return {
            id,
            ...formElementType,
            fieldName,
            instruction: '',
            required: false,
            data: (field.options || []).map((option) => ({
                text: option.label,
                value: ID.uuid(),
            })),
            config: { templateConfig, rectConfig },
        };
    }

    return {
        id,
        ...formElementType,
        fieldName,
        instruction: '',
        required: false,
        data: [],
        config: { templateConfig, rectConfig },
    };
};

const usePdfFieldAutoDetection = ({
    formId,
    teamId,
    chatroomId,
    url,
    canvasRef,
    pdfDimensions,
    onAddField,
    onDetectionBatch,
}) => {
    const [autoDetecting, setAutoDetecting] = useState(false);
    const detectionInFlightRef = useRef(false);

    const handleAutoDetect = async () => {
        if (detectionInFlightRef.current) return;
        if (!url) {
            toast.error('Please upload a PDF first.');
            return;
        }
        if (!formId) {
            toast.error('Please save the form before detecting PDF fields.');
            return;
        }

        try {
            detectionInFlightRef.current = true;
            setAutoDetecting(true);
            showLoadingAlert('Detecting PDF fields...', { allowOutsideClick: false });
            const usageScope = new URLSearchParams();
            if (teamId) usageScope.set('team', teamId);
            if (chatroomId) usageScope.set('chatroom', chatroomId);
            const query = usageScope.toString();
            const response = await fetchWrapper.post(
                `forms/${formId}/detect-fields-auto-tagging${query ? `?${query}` : ''}`,
                {}
            );
            if (!response?.success || !response.fields?.length) {
                toast.warning(response?.message || 'No form fields detected in this PDF.');
                return;
            }

            const responseFields = response.fields;
            const totalFields = responseFields.length;
            showLoadingAlert(`Inserting fields (0/${totalFields})...`, { allowOutsideClick: false });
            const savedFields = [];
            let failedCount = 0;
            let usableCount = 0;

            for (let index = 0; index < totalFields; index += 1) {
                Swal.update(
                    { title: `Inserting fields (${index + 1}/${totalFields})...` },
                    { allowOutsideClick: false }
                );
                const detectedField = createDetectedFormElement({
                    field: responseFields[index],
                    detectionMetadata: response.metadata,
                    canvas: canvasRef.current,
                    pdfDimensions,
                });
                // Drop processed detection payloads so large responses can be reclaimed incrementally.
                responseFields[index] = null;

                if (!detectedField) {
                    continue;
                }

                usableCount += 1;
                // Save serially to avoid flooding the API and Redux with hundreds of field updates.
                // eslint-disable-next-line no-await-in-loop
                const savedField = await onAddField(detectedField);
                if (savedField) savedFields.push(savedField);
                else failedCount += 1;

                if (index < totalFields - 1) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((resolve) => setTimeout(resolve, AUTO_DETECT_INSERT_DELAY_MS));
                }
            }

            responseFields.length = 0;
            onDetectionBatch?.(savedFields);
            if (savedFields.length > 0) {
                toast.success(`Auto-detected ${savedFields.length} form fields! Review and adjust as needed.`);
            }
            if (failedCount > 0) {
                toast.warning(`${failedCount} detected field${failedCount === 1 ? '' : 's'} could not be saved.`);
            } else if (usableCount === 0) {
                toast.warning('Detected fields did not include usable coordinates.');
            } else if (savedFields.length === 0) {
                toast.warning('Detected fields could not be saved. Please try again.');
            }
        } catch (error) {
            console.error('Auto-detect error:', error);
            toast.error(error?.message || 'Failed to auto-detect fields. Try manual mapping.');
        } finally {
            Swal.close();
            detectionInFlightRef.current = false;
            setAutoDetecting(false);
        }
    };

    return { autoDetecting, handleAutoDetect };
};

export default usePdfFieldAutoDetection;
